import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  TableContainer,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { saveAs } from 'file-saver';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../styles/Service.scss';
import '../../styles/App.scss';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  ADD_SERVICE,
  EDIT_SERVICE,
  VIEW_SERVICE,
} from '../../component/common/Routes/AppRoutes';
import SearchAndButtonTab from '../../component/ui/SearchAndButtonTab';
import { formatDateWithAmPm, getStatusClass } from '../../Style';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetServiceDetails,
  serviceDeleteRequest,
  serviceViewAllRequest,
} from '../../redux/Service/ServiceAction';
import { toast, ToastContainer } from 'react-toastify';
import CustomButton from '../../component/ui/FormFields/Button';
import nodata from '../../assets/nodata.jpg';
import Loading from '../../component/ui/Loading';
import axiosInstance from '../../redux/instance/AxiosInstance';

const ServiceList = () => {
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  const tab = location.state?.tab ?? 0;
  const pageNumber= location.state?.page ?? 1;


  const permissions = useMemo(() => {
    let safePermissions = {};

    try {
      if (user?.permissions) {
        safePermissions =
          typeof user.permissions === 'string'
            ? JSON.parse(user.permissions)
            : user.permissions;
      }
    } catch (error) {
      safePermissions = {};
    }

    return safePermissions;
  }, [user]);

  const searchNavigateQuery=location.state?.searchQuery??'';
  const [searchQuery, setSearchQuery] = useState(searchNavigateQuery??"");
  const [searchColumn, setSearchColumn] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [page, setPage] = useState(pageNumber);
  const [selectedTab, setSelectedTab] = useState(tab);
  const rowsPerPage = 10;
  const searchTimerRef = useRef(null);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');


  const dispatch = useDispatch();

  const serviceDetail = useSelector(
    (state) => state.services?.ServiceDetails || {}
  );

  const viewListLoading = serviceDetail?.loading;

  const DeleteServiceDetails = useSelector(
    (state) => state.services?.Service || {}
  );
  const deleteLoading = DeleteServiceDetails?.deleteLoading;
  const serviceDetails = serviceDetail?.data || [];
  const deleteServiceSuccess = DeleteServiceDetails?.success;
  const totalSize = serviceDetail?.totalRecords || 0;
  const tableHeaderMapping = useMemo(
    () => ({
      'Service Name': 'services',
      'Service Type': 'type_of_service',
      'Arrival/Departure': 'type_of_name',
      Status: 'status',
      'Approved Status': 'approoved_status',
      'Created Date': 'created_at',
    }),
    []);
  const tableHeaders = useMemo(() => {
    const baseHeaders = [
      'Service Name',
      'Service Type',
      'Arrival/Departure',
      'Status',
      'Approved Status',
      'Created Date',
    ];

    const hasServiceActions =
      user?.user_type === 1 ||
      permissions?.services?.view ||
      permissions?.services?.edit ||
      permissions?.services?.delete;

    return hasServiceActions ? [...baseHeaders, 'Actions'] : baseHeaders;
  }, [user, permissions]);
  const columns = useMemo(
    () => [
      { value: 'services', label: 'SERVICE NAME' },
      { value: 'type_of_service', label: 'SERVICE TYPE' },
      { value: 'type_of_name', label: 'ARRIVAL/DEPARTURE' },
    ],
    []
  );
  const sortedServices = useMemo(() => {
    if (!serviceDetails || !serviceDetails.length) return [];

    const services = [...serviceDetails];

    if (!orderBy) return services;

    return services.sort((a, b) => {
      let valA, valB;

      if (orderBy === 'created_at') {
        valA = new Date(a.created_at || 0);
        valB = new Date(b.created_at || 0);
      } else if (orderBy === 'status' || orderBy === 'approoved_status') {
        valA = Number(a[orderBy] || 0);
        valB = Number(b[orderBy] || 0);
      } else {
        valA = (a[orderBy] || '').toString().toLowerCase();
        valB = (b[orderBy] || '').toString().toLowerCase();
      }
      if (orderBy === 'created_at') {
        return order === 'asc' ? valA - valB : valB - valA;
      } else if (typeof valA === 'number' && typeof valB === 'number') {
        return order === 'asc' ? valA - valB : valB - valA;
      } else {
        if (valA < valB) return order === 'asc' ? -1 : 1;
        if (valA > valB) return order === 'asc' ? 1 : -1;
        return 0;
      }
    });
  }, [serviceDetails, orderBy, order]);

  const totalPages = Math.ceil(totalSize / rowsPerPage);

  const fetchServices = (
    pageNumber = page,
    tabIndex = selectedTab,
    search = searchQuery,
    searchBy = searchColumn
  ) => {
    const approoveStatus = tabIndex == 0 ? 2 : tabIndex == 1 ? 1 : 3;
 
    const params = {
      approoved_status: approoveStatus,
    };

    if (search) {
      params.pagination_required = true;
      params.pagesize = rowsPerPage;
      params.pageNo = pageNumber;
      params.search = search;

      if (searchBy && searchBy.trim() !== '') {
        params.search_by = searchBy;
      }
    } else {
      params.pagination_required = true;
      params.pagesize = rowsPerPage;
      params.pageNo = pageNumber;
    }

    dispatch(serviceViewAllRequest(params));
  };

  const handleSearch = (query, column) => {
    setSearchQuery(query);

    if (column !== undefined) {
      setSearchColumn(column);
    }

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (query) {
      searchTimerRef.current = setTimeout(() => {
        setPage(1);
        fetchServices(
          1,
          selectedTab,
          query,
          column !== undefined ? column : searchColumn
        );
      }, 1000);
    } else if (query === '' && searchQuery !== '') {
      searchTimerRef.current = setTimeout(() => {
        setPage(1);
        fetchServices(
          1,
          selectedTab,
          '',
          column !== undefined ? column : searchColumn
        );
      }, 1000);
    }
  };
  const handleColumnChange = (column) => {
    setSearchColumn(column);
  };

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  const isInitialLoad = useRef(true);


  useEffect(() => {
    if (isInitialLoad.current&!searchNavigateQuery) {
      dispatch(resetServiceDetails());
      fetchServices(1, selectedTab, '', '');
      isInitialLoad.current = false;
    } else {
      fetchServices(page, selectedTab, searchQuery, searchColumn);
    }
  }, [selectedTab, page, dispatch]);

  useEffect(() => {
    if (deleteServiceSuccess) {
      toast.success('Service Deleted Successfully', {
        position: 'top-center',
        autoClose: 3000,
        closeOnClick: true,
        pauseOnHover: false,
        onClose: () => {
          dispatch(resetServiceDetails());
          fetchServices(1, selectedTab);
        },
      });
    }
  }, [deleteServiceSuccess, selectedTab, dispatch]);

  const handleClosePopup = () => {
    setSelectedService(null);
    setOpen(false);
  };

  const handleConfirmDelete = async (deleteValue) => {
    await dispatch(serviceDeleteRequest(deleteValue.id));
    handleClosePopup();
  };

  const handleExport = () => {
    const approoveStatus = selectedTab == "0" ? 2 : selectedTab == "1" ? 1 : 3;

    const params = {
      approoved_status: approoveStatus,
      pagination_required: false,
    };
    if (searchQuery) {
      params.pagination_required = false;
      params.search = searchQuery;

      if (searchColumn && searchColumn.trim() !== '') {
        params.search_by = searchColumn;
      }
    } else {
      params.pagination_required = false;
    }
    const apiCall = axiosInstance.get(`/services/list`, { params });

    apiCall
      .then((response) => {
        const allServicesData = response?.data?.data || [];
        if (!serviceDetails.length) return;

        const csvHeaders = [
          'Service Name,Service Type,Airport Type,Status,Approval Status,Created Date',
        ];

        const csvRows = allServicesData.map((service) => {
          const escapeCsvField = (field) => {
            if (field === null || field === undefined) return '""';
            const stringField = String(field || '-');
            if (
              stringField.includes(',') ||
              stringField.includes('"') ||
              stringField.includes('\n')
            ) {
              return `"${stringField.replace(/"/g, '""')}"`;
            }
            return stringField;
          };

          const status = service.status == 1 ? 'Active' : 'Inactive';
          const approvalStatus =
            service.approoved_status === 2
              ? 'Pending'
              : service.approoved_status === 1
              ? 'Approved'
              : 'Rejected';

          return [
            escapeCsvField(service.services),
            escapeCsvField(service.type_of_service?.toUpperCase()),
            escapeCsvField(service.type_of_name?.toUpperCase()),
            escapeCsvField(status),
            escapeCsvField(approvalStatus),
            escapeCsvField(formatDateWithAmPm(service.created_at)),
          ].join(',');
        });

        const csvContent = [csvHeaders, ...csvRows].join('\n');
        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        saveAs(blob, 'services.csv');
      })
      .catch((error) => {
        console.error('Export error:', error);
      });
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setPage(1);
    dispatch(resetServiceDetails());
  };

  const handleOpenDialog = (service) => {
    setSelectedService(service);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setSelectedService(null);
    setOpen(false);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const navigate = useNavigate();
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const renderSortIcon = (property) => {
    if (orderBy !== property) {
      return null;
    }
    return order === 'asc' ? (
      <ArrowUpwardIcon fontSize='small' style={{ marginLeft: '5px' }} />
    ) : (
      <ArrowDownwardIcon fontSize='small' style={{ marginLeft: '5px' }} />
    );
  };

  const renderActions = (service) => {
    const hasAnyAction =
      user?.user_type === 1 ||
      permissions?.services?.view ||
      permissions?.services?.edit ||
      permissions?.services?.delete;

    if (!hasAnyAction) return null;
    return (
      <Box className='button-list'>
        {(permissions?.services?.view || user?.user_type === 1) && (
          <Button
            variant='outlined'
            size='small'
            className='view-user-button'
            onClick={() =>
              navigate(`${VIEW_SERVICE.replace(':serviceId', service.id)}`,{state:{tab:selectedTab,page:page,searchQuery:searchQuery}})
            }
          >
            View
          </Button>
        )}
        {(permissions?.services?.edit || user?.user_type === 1) && (
          <Button
            variant='outlined'
            className='edit-user-button'
            size='small'
            onClick={() =>
              navigate(`${EDIT_SERVICE.replace(':serviceId', service.id)}`,{state:{tabEdit:selectedTab,pageEdit:page,searchQuery:searchQuery}})
            }
          >
            Edit
          </Button>
        )}
        {(permissions?.services?.delete || user?.user_type === 1) && (
          <CustomButton
            variant='contained'
            className='delete-user-button'
            type='button'
            text={'DELETE'}
            size='small'
            onClick={() => handleOpenDialog(service)}
          />
        )}
      </Box>
    );
  };

  if (viewListLoading) {
    return <Loading text='Loading service details...' />;
  }

  return (
    <>
      <ToastContainer />
      <Box className='container'>
        <Grid container spacing={5}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <SearchAndButtonTab
                  title='Services'
                  user={user}
                  hideSearchColumnSelector={false}
                  searchColumn={searchColumn}
                  setSearchColumn={handleColumnChange}
                  searchQuery={searchQuery}
                  setSearchQuery={handleSearch}
                  columns={columns}
                  handleExport={handleExport}
                  routeName={ADD_SERVICE}
                  permissions={permissions}
                  type='services'
                  searchPlaceholder='Search by ID, Name, Service Type or Airport Type'
                />
                <Tabs
                  value={selectedTab}
                  onChange={handleTabChange}
                  aria-label='Approval Status Tabs'
                  sx={{ marginBottom: 2 }}
                >
                  <Tab label='Pending' />
                  <Tab label='Approved' />
                  <Tab label='Rejected' />
                </Tabs>

                <TableContainer component={Paper}>
                  <PerfectScrollbar>
                    {sortedServices && sortedServices.length > 0 ? (
                      <Table>
                        <TableHead>
                          <TableRow>
                            {tableHeaders.map((header) => (
                              <TableCell
                                key={header}
                                onClick={() => {
                                  if (
                                    header !== 'Actions' &&
                                    tableHeaderMapping[header]
                                  ) {
                                    handleSort(tableHeaderMapping[header]);
                                  }
                                }}
                                style={{
                                  cursor:
                                    header !== 'Actions'
                                      ? 'pointer'
                                      : 'default',
                                  userSelect: 'none',
                                }}
                              >
                                <Box display='flex' alignItems='center'>
                                  {header}
                                  {header !== 'Actions' &&
                                    tableHeaderMapping[header] &&
                                    renderSortIcon(tableHeaderMapping[header])}
                                </Box>
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedServices.map((service) => (
                            <TableRow key={service.service_id || service.id}>
                              <TableCell>{service.services}</TableCell>
                              <TableCell>
                                {service.type_of_service?.toUpperCase()}
                              </TableCell>
                              <TableCell>
                                {service.type_of_name?.toUpperCase()}
                              </TableCell>
                              <TableCell>
                                <span
                                  className={`badge ${getStatusClass(
                                    service.status == 1 ? 'Active' : 'Inactive'
                                  )}`}
                                >
                                  {service.status == 1 ? 'Active' : 'Inactive'}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span
                                  className={`badge ${getStatusClass(
                                    service.approoved_status === 2
                                      ? 'Pending'
                                      : service.approoved_status === 1
                                      ? 'Approved'
                                      : 'Rejected'
                                  )}`}
                                >
                                  {service.approoved_status === 2
                                    ? 'Pending'
                                    : service.approoved_status === 1
                                    ? 'Approved'
                                    : 'Rejected'}
                                </span>
                              </TableCell>
                              <TableCell>
                                {formatDateWithAmPm(service.created_at)}
                              </TableCell>
                              {tableHeaders.includes('Actions') && (
                                <TableCell>{renderActions(service)}</TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '100%',
                          padding: '50px 0',
                        }}
                      >
                        <img
                          src={nodata}
                          alt='nodata'
                          style={{ maxWidth: '300px', maxHeight: '300px' }}
                        />
                      </div>
                    )}
                  </PerfectScrollbar>
                </TableContainer>
                <Box className='button-box'>
                  {totalSize > 0 && (
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color='primary'
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Dialog open={open} onClose={handleCloseDialog}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete{' '}
              <strong>{selectedService?.services}</strong>?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button className='grey-button' onClick={handleCloseDialog}>
              Cancel
            </Button>
            <CustomButton
              variant='contained'
              className='orange-button'
              type='button'
              loading={deleteLoading}
              text={'DELETE'}
              onClick={() => handleConfirmDelete(selectedService)}
            />
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};
export default ServiceList;
